import { SizeType } from 'antd/es/config-provider/SizeContext';
import React, {
  FC,
  RefObject,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectId, TaskId } from 'api/models';
import { StatusUpdateState } from 'api/status-updates';
import { Task as TaskApi } from 'api/tasks';
import { ReactComponent as NoContentSvg } from 'assets/svgs/empty-states/no-content-1.svg';
import { Empty } from 'components/atoms/empty';
import { InputType } from 'components/atoms/input';
import { AddFormPopover } from 'components/organisms/projects/project-plan/swimlane/add-form-popover';
import { RbacButton } from 'components/organisms/rbac/components/rbac-buttons';
import { RbacFormattedMessage } from 'components/organisms/rbac/components/rbac-intl';
import { RbacNewRow } from 'components/organisms/rbac/components/rbac-new-row';
import { useProjectDetail } from 'data/projects';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { Task } from 'data/tasks';
import {
  updateTaskSagaAction,
  deleteTaskSagaAction,
  removeAsSubtaskSagaAction,
  createSubTaskSagaAction,
} from 'data/tasks/actions';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import { getProjectTasksQueryKey } from 'queries/common';
import { queueMacrotask } from 'utils/macrotask';
import { AddTaskOrFormRow } from './add-task-form-row';
import { Table } from './table';
import { TaskPicker } from './tasks-picker';
import styles from './tasks-table.module.css';

export const AddForm = ({
  showCreateFormPopover,
  toggleShowCreateFormPopover,
  projectId,
  onAddNewForm,
}: {
  showCreateFormPopover: boolean;
  projectId: number;
  onAddNewForm: (name: string, id: number) => Promise<void> | undefined;

  toggleShowCreateFormPopover: React.Dispatch<React.SetStateAction<boolean>>;
}) => (
  <AddFormPopover
    visible={showCreateFormPopover}
    toggle={toggleShowCreateFormPopover}
    projectId={projectId}
    onSubmit={onAddNewForm}
  />
);

interface Props {
  size?: SizeType;
  projectId: number;
  data: Array<Task>;
  refTo?: RefObject<HTMLDivElement>;
  onTaskNameClick: (task: TaskId) => void;
  onDueDateChange?: (
    taskId: number,
    payload: {
      startDate?: number;
      dueDate?: number;
    },
  ) => void;
  onAssigneeChange?: (taskId: number, payload) => void;
  onDelete?: (task: Task) => void;
  handleRemoveTaskInStatusUpdate?: (task: Task) => Promise<void>;
  handleAddingNewTask?: (
    taskName: string,
    updateLocalState?: (newSubTask: TaskApi) => void,
  ) => Promise<void>;
  handleMultipleTasksAdded?: (tasks: Array<string>) => Promise<void>;
  documentSectionId?: number;
  resourceSectionId?: number;
  newRowPlaceholder?: string;
  hideEmptyStateImage?: boolean;
  isPrivate?: boolean;
  onSort?: (sorted: Task[]) => void;
  isSubTask?: boolean;
  isReadOnly?: boolean;
  autoFocus?: boolean;
  useClearAction?: boolean;
  isExpanded?: boolean;
  canAddFormAsSubTask?: boolean;
  taskId?: number;
}

export const TaskTable: FC<Props> = (props) => {
  const {
    size = 'middle',
    projectId,
    data = [],
    refTo,
    handleAddingNewTask,
    handleMultipleTasksAdded,
    newRowPlaceholder,
    onDelete,
    handleRemoveTaskInStatusUpdate,
    onDueDateChange,
    onAssigneeChange,
    hideEmptyStateImage = false,
    isPrivate,
    isSubTask,
    isReadOnly,
    resourceSectionId,
    documentSectionId,
    useClearAction,
    isExpanded,
    canAddFormAsSubTask,
    taskId: taskIdValue,
    ...rest
  } = props;

  const [canShowEmpty, setCanShowEmpty] = useState<boolean>(!data.length);
  const [canShowNewRow, setCanShowNewRow] = useState(false);
  const [showCreateFormPopover, toggleShowCreateFormPopover] = useState(false);
  const [showAddSubTask, toggleShowAddSubTask] = useState(false);
  const [dataSource, setDataSource] = useState<Task[]>(data ?? []);
  const [isNewSubTaskCreating, setNewSubTaskCreating] = useState(false);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const updateLocalState = (newSubTask: TaskApi) => {
    setDataSource((subTasksData) => [
      ...subTasksData,
      {
        ...newSubTask,
        project: newSubTask.project.projectId,
      } as unknown as Task,
    ]);
    setNewSubTaskCreating(false);
  };

  useEffect(() => {
    if (isExpanded === false) {
      toggleShowAddSubTask(false);
    }
  }, [isExpanded]);

  const handleNewTaskAdded = useCallback(
    (taskName: string) => {
      if (handleAddingNewTask) {
        return handleAddingNewTask(taskName, updateLocalState);
      }

      if (taskIdValue) {
        setNewSubTaskCreating(true);
        dispatch(
          createSubTaskSagaAction({
            projectId,
            taskName,
            parentTask: taskIdValue,
            queryClient,
            updateLocalState,
          }),
        );
      }

      return Promise.resolve();
    },
    [dispatch, handleAddingNewTask, projectId, queryClient, taskIdValue],
  );

  const handleNewFormAdded = useCallback(
    (name: string, id: number) => {
      if (taskIdValue) {
        setNewSubTaskCreating(true);
        dispatch(
          createSubTaskSagaAction({
            projectId,
            taskName: name,
            formTemplateId: id,
            parentTask: taskIdValue,
            queryClient,
            updateLocalState,
          }),
        );

        return Promise.resolve();
      }

      return Promise.reject();
    },
    [dispatch, projectId, queryClient, taskIdValue],
  );

  const activeStatusUpdate = useSelector(selectActiveStatusUpdate);
  const pathName = window.location.pathname;
  const isPublished =
    pathName.includes('status-updates') &&
    activeStatusUpdate?.state === StatusUpdateState.Published;
  const isFromDocument =
    pathName.includes('spaces') || pathName.includes('status-updates');
  const hideByUrl = isFromDocument;

  const ruleForAddTasks = isFromDocument
    ? // ? 'DOCUMENT_TASK:CREATE'
      'TASK:CREATE'
    : 'SUBTASK:CREATE';

  useEffect(() => {
    setCanShowEmpty(!data.length);
  }, [data.length]);

  const handleAssigneeChange = (
    taskId: number,
    assignee,
    resolveCallback?: (value) => void,
  ) => {
    if (onAssigneeChange) {
      onAssigneeChange(taskId, { assignee });
    } else {
      dispatch(
        updateTaskSagaAction({
          projectId,
          taskId,
          data: {
            assignee,
          },
          options: {
            resolveCallback,
          },
        }),
      );
    }
  };

  const handleDueDateChange = (taskId, payload) => {
    if (onDueDateChange) {
      onDueDateChange(taskId, payload);
    } else {
      dispatch(updateTaskSagaAction({ projectId, taskId, data: payload }));
    }
  };

  const handleDelete = async (task: Task) => {
    if (onDelete) {
      onDelete(task);
    } else {
      const tasks: TaskApi[] | undefined = await queryClient.getQueryData(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );

      queryClient.setQueryData(
        getProjectTasksQueryKey(ProjectId(projectId)),
        tasks?.filter((taskValue) => taskValue.taskId === task.taskId) ?? [],
      );
      dispatch(deleteTaskSagaAction({ projectId, taskId: task.taskId }));
    }
  };

  const handleRemoveAsSubtask = async (_task: Task) => {
    const tasks: TaskApi[] | undefined = await queryClient.getQueryData(
      getProjectTasksQueryKey(ProjectId(projectId)),
    );

    queryClient.setQueryData(
      getProjectTasksQueryKey(ProjectId(projectId)),
      tasks?.map((taskValue) => {
        if (taskValue.taskId === _task.taskId) {
          return {
            ...taskValue,
            parentTaskId: undefined,
            parentTaskObject: undefined,
          };
        }

        return taskValue;
      }) ?? [],
    );

    dispatch(
      removeAsSubtaskSagaAction({
        projectId,
        taskId: _task.taskId,
        queryClient,
        parentTaskId: _task.parentTask,
        phaseId: _task.projectPhase?.projectPhaseId,
      }),
    );
  };

  const inputRef = useRef<InputType>(null);
  const handleAddClick = () => {
    if (hideByUrl) {
      setCanShowNewRow(true);
    }
    setCanShowEmpty(false);
    queueMacrotask(() => inputRef.current?.focus());
  };

  const handleAddFormClick = () => {
    toggleShowCreateFormPopover(true);
  };

  const onClear = () => {
    setCanShowNewRow(false);
    setCanShowEmpty(!data.length);
  };

  const { data: projectDetail } = useProjectDetail(ProjectId(projectId));

  const projectResource = useMemo(
    () => (projectDetail ? ProjectResource(projectDetail) : undefined),
    [projectDetail],
  );
  const { hasAccessToResource } = useWithAccess();
  const canAddTasks = hasAccessToResource({
    rule: ruleForAddTasks,
    resourceData: projectResource,
  });

  const onAddNewTask = useCallback(
    (taskName: string) => {
      toggleShowAddSubTask(false);

      return handleNewTaskAdded(taskName);
    },
    [handleNewTaskAdded],
  );

  const onAddNewForm = useCallback(
    (name: string, id: number) => {
      toggleShowCreateFormPopover(false);

      return handleNewFormAdded?.(name, id);
    },
    [handleNewFormAdded],
  );

  const handleRemoveTaskInStatusUpdateHandler = useCallback(
    async (task: Task) => {
      await handleRemoveTaskInStatusUpdate?.(task);
      setDataSource((_dataSource) =>
        _dataSource.filter((item) => item.taskId !== task.taskId),
      );
    },
    [handleRemoveTaskInStatusUpdate],
  );

  const addTasksSection = () => (
    <div className={styles.add_task}>
      <RbacButton
        ref={refTo}
        onClick={isReadOnly ? undefined : handleAddClick}
        className={styles.add_btn}
        type="link"
        size="small"
        rule={ruleForAddTasks}
        resourceData={projectResource}
        data-cy="add_task.button"
      >
        <FormattedMessage id="task.add" />
      </RbacButton>
      {canAddFormAsSubTask ? (
        <>
          <FormattedMessage id="or" />
          <RbacButton
            ref={refTo}
            onClick={isReadOnly ? undefined : handleAddFormClick}
            className={styles.add_btn}
            type="link"
            size="small"
            rule={ruleForAddTasks}
            resourceData={projectResource}
            data-cy="add_form.button"
          >
            <FormattedMessage id="form.add" />
          </RbacButton>
          <AddForm
            showCreateFormPopover={showCreateFormPopover}
            projectId={projectId}
            onAddNewForm={onAddNewForm}
            toggleShowCreateFormPopover={toggleShowCreateFormPopover}
          />
        </>
      ) : null}

      {resourceSectionId && documentSectionId ? (
        <div>
          <RbacFormattedMessage
            id="task.new.or.existing"
            rule={ruleForAddTasks}
            resourceData={projectResource}
          />

          <TaskPicker
            projectId={projectId}
            isPrivate={isPrivate}
            resourceSectionId={resourceSectionId}
            documentSectionId={documentSectionId}
            isReadOnly={isReadOnly}
            setDataSource={setDataSource}
          />
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={isPrivate ? styles.private : ''}>
      {!canShowEmpty ? (
        <div ref={refTo}>
          {data.length > 0 && (
            <Table
              handleDueDateChange={handleDueDateChange}
              handleAssigneeChange={handleAssigneeChange}
              handleDelete={handleDelete}
              size={size}
              isSubTask={isSubTask}
              isSuPublished={isPublished}
              isReadOnly={isReadOnly}
              isFromDocument={isFromDocument}
              handleRemoveAsSubtask={handleRemoveAsSubtask}
              handleRemoveTaskInStatusUpdate={
                handleRemoveTaskInStatusUpdateHandler
              }
              {...rest}
              projectId={projectId}
              dataSource={dataSource}
              setDataSource={setDataSource}
            />
          )}
          {(!hideByUrl || (!isPublished && canShowNewRow)) && !isReadOnly ? (
            showAddSubTask ? (
              <RbacNewRow
                autoFocus
                inputRef={inputRef}
                size={size}
                onAdd={onAddNewTask}
                onAddMultiple={handleMultipleTasksAdded}
                placeholder={newRowPlaceholder}
                onClear={onClear}
                useClearAction={useClearAction}
                bulkAddType="subtasks"
                showStatusIcon
                rule={ruleForAddTasks}
                resourceData={projectResource}
              />
            ) : (
              <AddTaskOrFormRow
                isSubTaskCreating={isNewSubTaskCreating}
                showCreateFormPopover={showCreateFormPopover}
                projectId={projectId}
                onAddNewForm={onAddNewForm}
                toggleShowAddSubTask={toggleShowAddSubTask}
                toggleShowCreateFormPopover={toggleShowCreateFormPopover}
              />
            )
          ) : null}
          {hideByUrl && !isPublished && !canShowNewRow && canAddTasks
            ? addTasksSection()
            : null}
        </div>
      ) : (
        <Empty hideImage={hideEmptyStateImage} isPrivate={isPrivate}>
          {(!hideByUrl || !isPublished) && canAddTasks ? (
            addTasksSection()
          ) : (
            <FormattedMessage id="no_tasks_available" />
          )}
          <NoContentSvg />
        </Empty>
      )}
    </div>
  );
};
